import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Text } from 'grommet'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  Divider,
  FormButton,
  FormInputField,
  FormStep,
  SelectInputField,
  SmallText,
  TextAreaField
} from '../../../../../components/common'
import { Modal } from '../../../../../components/Modals'
import { lateAdministeredArray, selectableMarStatusOptions } from '../../../../../constants'
import { useMedAdministration } from '../../../../../hooks/useMedAdminist'
import { useAppSelector } from '../../../../../store/hooks'
import { MARResMock } from '../../../../../types/mar.type'
import { WardStockOption } from '../../../../../types/wardstock.type'
import { capitalizeDrugName, concatenateStrengthsConcise, getError, pluralize } from '../../../../../utils'
import { buildMarUpdatePayload, consumesStock, requiresReason } from '../../../../../utils/marAdministration'
import { isLateDose } from '../../../../../utils/marLate'
import { showToast } from '../../../../../utils/toast'
import { marSchema } from '../../../../../validation/mar.schema'

type Props = {
  onClose: () => void
  id?: string
  defaultData?: MARResMock
}

export const EditMAR = ({ onClose, defaultData, id }: Props) => {
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      quantity: defaultData?.attributes?.quantity ? Number(defaultData.attributes.quantity) : undefined,
      medication: defaultData?.attributes?.medication_details?.drug_name,
      note: '',
      status: '',
      ward_stock_id: '',
      ward_stock_quantity: undefined
    },
    resolver: yupResolver(marSchema)
  })
  const { user } = useAppSelector(state => state.user)
  const [step, setStep] = useState<number>(1)
  const [disableBtn, setDisableBtn] = useState(true)
  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { isSubmitting }
  } = methods
  const { updateMedicationAdministration, wardStockOptions = [] } = useMedAdministration({
    id,
    medication_id: defaultData?.relationships?.medication?.data?.id
  })
  const [btnType, setBtnType] = useState<'button' | 'submit'>('button')

  const medicationArray = useMemo(() => {
    return [defaultData?.attributes?.medication_details]
  }, [defaultData?.attributes?.medication_details])

  const isLateAdministered = useMemo(
    () => isLateDose(defaultData?.attributes?.scheduled_for),
    [defaultData?.attributes?.scheduled_for]
  )

  const handleStepChange = () => {
    if (step === 1) setStep(2)
    if (step === 2) setStep(3)
  }

  const medicationWatch = watch('medication')

  const selectedMed = useMemo(() => {
    return medicationArray.find((med: any) => med?.drug_name === medicationWatch)
  }, [medicationWatch, medicationArray])

  const doseQuantity = Number(selectedMed?.dose_quantity) || 0
  const displayUnit = pluralize(doseQuantity, selectedMed?.dose_unit || '')

  const statusWatch = watch('status')
  const quantity = watch('quantity')
  const wardStockId = watch('ward_stock_id')
  const noteWatch = watch('note')

  const shouldCaptureStockDetails = consumesStock(statusWatch)
  const reasonRequired = requiresReason(statusWatch)

  const selectedWardStock = useMemo(() => {
    return wardStockOptions.find((stock: WardStockOption) => `${stock.id}` === `${wardStockId}`)
  }, [wardStockId, wardStockOptions])

  const isQuantityAboveStock = useMemo(() => {
    if (!shouldCaptureStockDetails || !quantity || !selectedWardStock) {
      return false
    }
    return Number(quantity) > Number(selectedWardStock.stock_quantity)
  }, [quantity, selectedWardStock, shouldCaptureStockDetails])

  const medication = useMemo(() => {
    return !!medicationWatch
  }, [medicationWatch])

  const handleClose = () => {
    if (step === 3) return setStep(2)
    if (step === 2) return setStep(1)
    else {
      onClose()
      reset()
    }
  }

  const onSubmit = async (data: any) => {
    const payload = buildMarUpdatePayload({
      note: data.note,
      quantity: data.quantity,
      source: selectedWardStock,
      staffId: user?.id,
      status: data.status
    })

    try {
      await updateMedicationAdministration({ formVal: payload, id: id || '' })
      showToast('MedicationAdministration updated successfully')
      onClose()
    } catch (error: any) {
      getError(error)
    }
  }

  useEffect(() => {
    if (step === 3) {
      const timer = setTimeout(() => {
        setBtnType('submit')
      }, 500)

      return () => clearTimeout(timer)
    } else {
      setBtnType('button')
    }
  }, [step])

  useEffect(() => {
    setValue('ward_stock_quantity', selectedWardStock?.stock_quantity, { shouldValidate: true })
  }, [selectedWardStock, setValue])

  useEffect(() => {
    if (!shouldCaptureStockDetails) {
      setValue('quantity', undefined)
      setValue('ward_stock_id', '')
      setValue('ward_stock_quantity', undefined)
    }
  }, [setValue, shouldCaptureStockDetails])

  // Set the default status to late_administered and disable it if the condition is met
  useEffect(() => {
    if (isLateAdministered) {
      setValue('status', 'late_administered', { shouldValidate: true })
    }
  }, [isLateAdministered, setValue])

  useEffect(() => {
    if (step === 1 && medication) {
      setDisableBtn(false)
    } else if (
      step === 2 &&
      !!statusWatch &&
      (shouldCaptureStockDetails ? !!quantity && !!wardStockId && !isQuantityAboveStock : true)
    ) {
      setDisableBtn(false)
    } else if (step === 3 && (!reasonRequired || !!noteWatch?.trim())) {
      setDisableBtn(false)
    } else {
      setDisableBtn(true)
    }
  }, [
    step,
    medication,
    statusWatch,
    quantity,
    shouldCaptureStockDetails,
    wardStockId,
    isQuantityAboveStock,
    reasonRequired,
    noteWatch
  ])

  // Determine the current options to show based on late status or fallback
  const currentStatusOptions = isLateAdministered ? lateAdministeredArray : selectableMarStatusOptions

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} reset={reset} title="Administer Medication">
        <form className="flex flex-col md:w-[520px] w-full overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-auto flex flex-col px-8 py-4 w-[520px]">
            <Box align="center" direction="row" gap={'16px'} margin={{ bottom: '32px' }}>
              <FormStep check={medication} label="Medication" step={1} />
              <Divider style={{ maxWidth: '48px' }} />
              <FormStep check={!!statusWatch} label="Administration" step={2} />
              <Divider style={{ maxWidth: '48px' }} />
              <FormStep check={step !== undefined && step > 3} label="Notes" step={3} />
            </Box>
            <div className="overflow-auto">
              <Box style={{ minHeight: '344px', overflow: 'hidden', position: 'relative' }}>
                <div
                  className={`step-slide ${step === 1 ? 'active' : ''}`}
                  style={{
                    position: 'absolute',
                    transform: `translateX(${step === 1 ? '0' : '-100%'})`,
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  <Box margin={{ bottom: '20px' }}>
                    <SelectInputField
                      disabled={!(selectedMed?.medication_frequency_code === 'PRN')}
                      label="Medication"
                      labelKey={'drug_name'}
                      name="medication"
                      options={medicationArray}
                      placeholder="Select a medication"
                      valueKey={{ key: 'drug_name', reduce: true }}
                    />
                  </Box>
                  <Box background={'#EEF4FE'} pad={'16px'} round="8px">
                    <p className="text-base text-black">Medication Details</p>
                    <Box gap={'20px'} margin={{ bottom: '20px', top: '12px' }}>
                      <Box gap={'4px'}>
                        <Text color={'black'} size="small">
                          Name
                        </Text>
                        <Text color={'#333333'} size="small">
                          {capitalizeDrugName(selectedMed?.drug_name)}
                        </Text>
                      </Box>
                      {(statusWatch === 'administered' || statusWatch === 'wasted') && (
                        <>
                          <Box>
                            <FormInputField
                              counter
                              disabled={!selectedWardStock}
                              label="Quantity"
                              name="quantity"
                              placeholder="Enter quantity"
                              type="number"
                            />
                          </Box>
                          <Box gap={'4px'}>
                            <Text color={'black'} size="small">
                              Dosage
                            </Text>
                            <Text color={'#333333'} size="small">
                              {`${doseQuantity > 0 ? `${doseQuantity} ${displayUnit}` : selectedMed?.strength || concatenateStrengthsConcise(selectedMed?.api_doses)}`}
                            </Text>
                          </Box>
                          <Box gap={'4px'}>
                            <Text color={'black'} size="small">
                              Frequency
                            </Text>
                            <Text color={'#333333'} size="small" style={{ textTransform: 'uppercase' }}>
                              {selectedMed?.medication_frequency_code} {selectedMed?.medication_type}
                            </Text>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </div>
                <div
                  className={`step-slide ${step === 2 ? 'active' : ''}`}
                  style={{
                    position: 'absolute',
                    transform: `translateX(${step === 2 ? '0' : step < 2 ? '100%' : '-100%'})`,
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  <Text color={'#333333'} size="small">
                    Add administration for <SmallText>{getValues('medication')}</SmallText>
                  </Text>
                  <div className="flex flex-col gap-8 mt-4">
                    <Box>
                      <SelectInputField
                        disabled={isLateAdministered}
                        label="Status"
                        labelKey={'name'}
                        name="status"
                        options={currentStatusOptions}
                        placeholder="Select status"
                        valueKey={{ key: 'value', reduce: true }}
                      />
                    </Box>
                    {shouldCaptureStockDetails && (
                      <>
                        <Box>
                          <FormInputField
                            counter
                            label="Quantity"
                            name="quantity"
                            placeholder="Enter quantity"
                            type="number"
                          />
                        </Box>
                        <Box>
                          <SelectInputField
                            label="Drug"
                            labelKey={(option: WardStockOption) =>
                              `${capitalizeDrugName(option.drug_name)}; ${option.expiry_date}; ${option.stock_quantity} ${option.inventory_unit}`
                            }
                            name="ward_stock_id"
                            options={wardStockOptions}
                            placeholder="Select drug"
                            valueKey={{ key: 'id', reduce: true }}
                          />
                        </Box>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className={`step-slide ${step === 3 ? 'active' : 'exit-right'}`}
                  style={{
                    position: 'absolute',
                    transform: `translateX(${step === 3 ? '0' : '100%'})`,
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  <Box className="">
                    <TextAreaField
                      label={reasonRequired ? 'Reason' : 'Notes (Optional)'}
                      name="note"
                      placeholder={reasonRequired ? 'Why was this dose refused or wasted?' : 'Add notes'}
                      style={{ minHeight: '260px' }}
                    />
                  </Box>
                </div>
              </Box>
            </div>
          </div>
          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Back'}
            disabled={disableBtn || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleStepChange}
            onClose={handleClose}
            submit={step === 3 ? 'Add' : 'Proceed'}
            type={btnType}
          />
        </form>
      </Modal>
    </FormProvider>
  )
}
